/**
 * Story 4.1: Global Sidebar Navigation
 * 
 * Persistent sidebar navigation visible on all authenticated routes.
 * 8 core navigation items with active route highlighting for 4 workflow patterns.
 * 
 * Responsive:
 *   Desktop (≥1024px): Expanded by default, 240px width
 *   Tablet (768-1023px): Collapsed by default (icons only), expand on hover
 *   Mobile (<768px): Hidden, accessed via hamburger menu
 * 
 * Integration: Rendered inside EmmaFirstLayout, to the LEFT of content-area.
 * Emma's PersistentEmmaInterface is fixed-right — no collision.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { WorkspaceSwitcher } from '../WorkspaceSwitcher';
import { cn } from '../../utils/cn';
import { getRoutePolicyForPath, isAuthorizedForPolicy } from '../../security/routePolicyManifest';
import { hasPermission, type AuthUser } from '../../types/roles';
import {
  Home,
  Database,
  Route as RouteIcon,
  Upload,
  FolderKanban,
  Settings,
  CreditCard,
  BarChart2,
  Users,
  UserPlus,
  UserCircle,
  ClipboardList,
  Shield,
  LogOut,
  ChevronLeft,
  Menu,
  X,
  Briefcase,
  FileCheck,
  FolderOpen,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface NavItem {
  id: string;
  label: string;
  path?: string;
  icon: React.ReactNode;
  group: 'workspace' | 'setup' | 'campaign' | 'settings';
  matchPatterns?: RegExp[];
  ariaLabel: string;
  action?: 'navigate' | 'logout';
  isVisible?: (user: AuthUser | null) => boolean;
}

interface NavGroup {
  key: string;
  label: string;
}

// ─── Navigation Configuration ─────────────────────────────────────────────────

const NAV_GROUPS: NavGroup[] = [
  { key: 'workspace', label: 'Workspace' },
  { key: 'setup', label: 'Setup' },
  { key: 'campaign', label: 'Campaign' },
  { key: 'settings', label: 'Settings' },
];

const NAV_ITEMS: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Home',
    path: '/dashboard',
    icon: <Home className="h-5 w-5 flex-shrink-0" />,
    group: 'workspace',
    ariaLabel: 'Navigate to Home Dashboard',
  },
  {
    id: 'data-foundation',
    label: 'Entity Manager',
    path: '/data-foundation',
    icon: <Database className="h-5 w-5 flex-shrink-0" />,
    group: 'setup',
    ariaLabel: 'Navigate to Entity Manager',
  },
  {
    id: 'upload-center',
    label: 'Upload Center',
    path: '/upload-center',
    icon: <Upload className="h-5 w-5 flex-shrink-0" />,
    group: 'setup',
    ariaLabel: 'Navigate to Upload Center',
  },
  {
    id: 'governance',
    label: 'Governance',
    path: '/governance',
    icon: <Shield className="h-5 w-5 flex-shrink-0" />,
    group: 'setup',
    ariaLabel: 'Navigate to Governance Dashboard',
  },
  {
    id: 'workflow',
    label: 'Workflow',
    path: '/workflow',
    icon: <RouteIcon className="h-5 w-5 flex-shrink-0" />,
    group: 'campaign',
    // AC 4.1.3: Handle 4 workflow route patterns
    matchPatterns: [
      /^\/workflow$/,
      /^\/workflow\/[^/]+$/,
      /^\/workflow\/[^/]+\/[^/]+$/,
      /^\/campaign\/[^/]+\/workflow(\/[^/]+)?$/,
    ],
    ariaLabel: 'Navigate to Campaign Workflow',
  },
  {
    id: 'campaigns',
    label: 'Campaigns',
    path: '/campaigns',
    icon: <FolderKanban className="h-5 w-5 flex-shrink-0" />,
    group: 'campaign',
    matchPatterns: [/^\/campaigns(\/.*)?$/],
    ariaLabel: 'Navigate to Campaign Management',
  },
  {
    id: 'hclp-workspace',
    label: 'HCLP Workspace',
    path: '/hclp/workspace',
    icon: <Briefcase className="h-5 w-5 flex-shrink-0" />,
    group: 'campaign',
    matchPatterns: [/^\/hclp(\/.*)?$/],
    ariaLabel: 'Navigate to HCLP Workspace',
  },
  {
    id: 'hclp-review',
    label: 'HCLP Review',
    path: '/hclp/review',
    icon: <FileCheck className="h-5 w-5 flex-shrink-0" />,
    group: 'campaign',
    matchPatterns: [/^\/hclp\/review$/],
    ariaLabel: 'Navigate to HCLP Review Queue',
  },
  {
    id: 'account-content',
    label: 'Account Content',
    path: '/upload/account',
    icon: <FolderOpen className="h-5 w-5 flex-shrink-0" />,
    group: 'setup',
    matchPatterns: [/^\/upload\/account$/],
    ariaLabel: 'Navigate to Account Content Upload',
  },
  {
    id: 'profile',
    label: 'Profile',
    path: '/profile',
    icon: <UserCircle className="h-5 w-5 flex-shrink-0" />,
    group: 'settings',
    matchPatterns: [/^\/profile$/],
    ariaLabel: 'Navigate to Profile Settings',
  },
  {
    id: 'settings',
    label: 'General',
    path: '/settings',
    icon: <Settings className="h-5 w-5 flex-shrink-0" />,
    group: 'settings',
    matchPatterns: [/^\/settings$/, /^\/settings\/password$/, /^\/settings\/integrations$/],
    ariaLabel: 'Navigate to General Settings',
  },
  {
    id: 'team-invitations',
    label: 'Team & Invitations',
    path: '/settings/team',
    icon: <UserPlus className="h-5 w-5 flex-shrink-0" />,
    group: 'settings',
    matchPatterns: [/^\/settings\/team$/],
    ariaLabel: 'Navigate to Team and Invitations',
    isVisible: (user) => Boolean(user) && hasPermission(user.role, 'user.view_list'),
  },
  {
    id: 'billing',
    label: 'Billing',
    path: '/settings/billing',
    icon: <CreditCard className="h-5 w-5 flex-shrink-0" />,
    group: 'settings',
    matchPatterns: [
      /^\/settings\/billing$/,
      /^\/settings\/upgrade$/,
      /^\/subscription$/,
      /^\/upgrade$/,
    ],
    ariaLabel: 'Navigate to Billing',
    isVisible: (user) =>
      Boolean(user) &&
      (hasPermission(user.role, 'org.view_billing') || hasPermission(user.role, 'subscription.view')),
  },
  {
    id: 'usage',
    label: 'Usage',
    path: '/settings/usage',
    icon: <BarChart2 className="h-5 w-5 flex-shrink-0" />,
    group: 'settings',
    matchPatterns: [/^\/settings\/usage$/, /^\/usage$/],
    ariaLabel: 'Navigate to Credit Usage',
    isVisible: (user) =>
      Boolean(user) &&
      (hasPermission(user.role, 'credit.view_usage') ||
        hasPermission(user.role, 'credit.view_balance') ||
        hasPermission(user.role, 'org.view_billing') ||
        user.role === 'partner_user' ||
        user.role === 'agency_operator'),
  },
  {
    id: 'partners',
    label: 'Partners',
    path: '/partners',
    icon: <Users className="h-5 w-5 flex-shrink-0" />,
    group: 'settings',
    ariaLabel: 'Navigate to Partner Management',
    isVisible: (user) =>
      Boolean(user) &&
      (hasPermission(user.role, 'agency.manage_assignments') || user.role === 'agency_operator'),
  },
  {
    id: 'partner-funnel',
    label: 'Partner Funnel',
    path: '/partner/funnel',
    icon: <BarChart2 className="h-5 w-5 flex-shrink-0" />,
    group: 'settings',
    ariaLabel: 'Navigate to Partner Activation Funnel',
    isVisible: (user) =>
      Boolean(user) &&
      (hasPermission(user.role, 'agency.manage_assignments') || user.role === 'org_admin'),
  },
  {
    id: 'audit-log',
    label: 'Audit Log',
    path: '/audit-log',
    icon: <ClipboardList className="h-5 w-5 flex-shrink-0" />,
    group: 'settings',
    ariaLabel: 'Navigate to Audit Log',
    isVisible: (user) => Boolean(user) && hasPermission(user.role, 'analytics.view_audit'),
  },
  {
    id: 'platform-admin',
    label: 'Platform Admin',
    path: '/platform-admin',
    icon: <Shield className="h-5 w-5 flex-shrink-0" />,
    group: 'settings',
    ariaLabel: 'Navigate to Platform Admin',
    isVisible: (user) => Boolean(user) && hasPermission(user.role, 'platform.manage_orgs'),
  },
];

// ─── Hooks ────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'cosellus-sidebar-collapsed';

function useSidebarCollapse(defaultCollapsed: boolean) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored !== null ? stored === 'true' : defaultCollapsed;
    } catch {
      return defaultCollapsed;
    }
  });

  const toggle = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // localStorage full or disabled — silently degrade
      }
      return next;
    });
  }, []);

  return { isCollapsed, toggle, setIsCollapsed };
}

function useResponsiveMode() {
  const [mode, setMode] = useState<'desktop' | 'tablet' | 'mobile'>(() => {
    if (typeof window === 'undefined') return 'desktop';
    const w = window.innerWidth;
    if (w < 768) return 'mobile';
    if (w < 1024) return 'tablet';
    return 'desktop';
  });

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 768) setMode('mobile');
      else if (w < 1024) setMode('tablet');
      else setMode('desktop');
    };
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return mode;
}

// ─── Active Route Detection ───────────────────────────────────────────────────

function getItemMatchScore(item: NavItem, pathname: string): number {
  if (!item.path) return -1;
  // Exact match
  if (pathname === item.path) return 300 + item.path.length;
  // Pattern match (workflow's 4 route patterns, settings variants, campaigns sub-routes)
  if (item.matchPatterns) {
    if (item.matchPatterns.some((p) => p.test(pathname))) {
      return 200 + item.path.length;
    }
  }
  // Sub-route match: /data-foundation/anything → activate data-foundation
  if (pathname.startsWith(item.path + '/')) return 100 + item.path.length;
  return -1;
}

function getActiveItemId(items: NavItem[], pathname: string): string | null {
  let bestId: string | null = null;
  let bestScore = -1;

  for (const item of items) {
    const score = getItemMatchScore(item, pathname);
    if (score > bestScore) {
      bestScore = score;
      bestId = item.id;
    }
  }

  return bestId;
}

function canViewNavItem(item: NavItem, user: AuthUser | null): boolean {
  if (item.action === 'logout') {
    return true;
  }
  if (item.isVisible) {
    return item.isVisible(user);
  }
  if (!item.path) {
    return true;
  }
  return isAuthorizedForPolicy(user, getRoutePolicyForPath(item.path));
}

// ─── Component ────────────────────────────────────────────────────────────────

export const GlobalSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const mode = useResponsiveMode();
  const { isCollapsed, toggle, setIsCollapsed } = useSidebarCollapse(mode === 'tablet');
  const [mobileOpen, setMobileOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const focusedIndex = useRef(0);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Ctrl+B toggle (AC 4.1.8)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        if (mode === 'mobile') {
          setMobileOpen((prev) => !prev);
        } else {
          toggle();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mode, toggle]);

  // Arrow key navigation (AC 4.1.8)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      const items = sidebarRef.current?.querySelectorAll<HTMLButtonElement>(
        '[data-sidebar-item]'
      );
      if (!items) return;

      let nextIndex = index;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = (index + 1) % items.length;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        nextIndex = (index - 1 + items.length) % items.length;
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = items.length - 1;
      }

      if (nextIndex !== index) {
        focusedIndex.current = nextIndex;
        items[nextIndex]?.focus();
      }
    },
    []
  );

  // Mobile touch gestures (swipe open/close)
  const touchStart = useRef<number | null>(null);
  useEffect(() => {
    if (mode !== 'mobile') return;

    const onTouchStart = (e: TouchEvent) => {
      touchStart.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (touchStart.current === null) return;
      const diff = e.changedTouches[0].clientX - touchStart.current;
      // Swipe right to open (from left edge)
      if (!mobileOpen && touchStart.current < 30 && diff > 60) {
        setMobileOpen(true);
      }
      // Swipe left to close
      if (mobileOpen && diff < -60) {
        setMobileOpen(false);
      }
      touchStart.current = null;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [mode, mobileOpen]);

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderNavItem = (item: NavItem, index: number, collapsed: boolean, active: boolean) => {
    return (
      <button
        key={item.id}
        data-sidebar-item
        data-testid={item.id === 'logout-nav' ? 'sidebar-nav-logout-link' : `sidebar-nav-${item.id}`}
        onClick={() => {
          if (item.action === 'logout') {
            logout();
            navigate('/login', { replace: true });
            return;
          }
          if (item.path) {
            if (item.id === 'dashboard' && user?.role === 'partner_user') {
              navigate('/partner/dashboard');
            } else {
              navigate(item.path);
            }
          }
        }}
        onKeyDown={(e) => handleKeyDown(e, index)}
        className={cn(
          'w-full flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors',
          'hover:bg-slate-700/60',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400/80',
          active && 'bg-emerald-600/20 text-emerald-400 font-medium',
          !active && 'text-emerald-200/60 hover:text-emerald-100'
        )}
        aria-label={item.ariaLabel}
        aria-current={active ? 'page' : undefined}
        tabIndex={0}
        title={collapsed ? item.label : undefined}
      >
        {item.icon}
        {!collapsed && (
          <span className="truncate">{item.label}</span>
        )}
      </button>
    );
  };

  const renderNavContent = (collapsed: boolean) => {
    const visibleNavItems = NAV_ITEMS.filter((i) => canViewNavItem(i, user));
    const activeItemId = getActiveItemId(visibleNavItems, location.pathname);
    let flatIndex = 0;
    return (
      <nav
        role="navigation"
        aria-label="Main navigation"
        className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2 py-3"
      >
        {NAV_GROUPS.map((group) => {
          const items = visibleNavItems.filter((i) => i.group === group.key);
          if (items.length === 0) return null;
          return (
            <div key={group.key} className="mb-2">
              {!collapsed && (
                <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 select-none flex items-center justify-between">
                  <span>{group.label}</span>
                </div>
              )}
              {items.map((item) => {
                const idx = flatIndex++;
                return renderNavItem(item, idx, collapsed, item.id === activeItemId);
              })}
            </div>
          );
        })}
      </nav>
    );
  };

  const renderAccountActions = (collapsed: boolean) => (
    <div className="shrink-0 border-t border-slate-700/50 px-2 py-2">
      <button
        type="button"
        data-testid="sidebar-nav-logout"
        data-sidebar-item
        onClick={() => {
          logout();
          navigate('/login', { replace: true });
        }}
        className={cn(
          'flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm text-emerald-200/60 transition-colors',
          'hover:bg-slate-700/60 hover:text-emerald-100',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400/80',
          collapsed && 'justify-center px-2'
        )}
        aria-label="Log out of your account"
        title="Log out"
      >
        <LogOut className="h-5 w-5 shrink-0 text-emerald-300/90" />
        {!collapsed && <span className="truncate">Log out</span>}
      </button>
    </div>
  );

  // ── Mobile: hamburger + drawer ──────────────────────────────────────────────

  if (mode === 'mobile') {
    return (
      <>
        {/* Hamburger button */}
        <button
          data-testid="sidebar-hamburger"
          onClick={() => setMobileOpen(true)}
          className={cn(
            'fixed top-3 left-3 z-[201] p-2 rounded-md',
            'bg-white dark:bg-slate-900 shadow-md border border-slate-200 dark:border-slate-700',
            'hover:bg-slate-100 dark:hover:bg-slate-800',
            'focus-visible:outline-2 focus-visible:outline-blue-500',
            mobileOpen && 'hidden'
          )}
          aria-label="Open navigation menu"
          aria-expanded={mobileOpen}
        >
          <Menu className="h-5 w-5 text-slate-700 dark:text-slate-300" />
        </button>

        {/* Backdrop overlay */}
        {mobileOpen && (
          <div
            data-testid="sidebar-backdrop"
            className="fixed inset-0 z-[202] bg-black/40 transition-opacity"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Slide-in drawer */}
        <aside
          ref={sidebarRef}
          data-testid="global-sidebar"
          className={cn(
            'fixed left-0 top-0 z-[203] flex h-full min-h-0 w-[280px] flex-col',
            'border-r border-slate-800',
            'shadow-xl transition-transform duration-300 ease-in-out',
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          )}
          style={{ background: 'linear-gradient(180deg, #0f2318 0%, #143024 50%, #1a3a30 100%)', color: '#d1e7dd' }}
          role="complementary"
          aria-label="Global navigation sidebar"
        >
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between border-b border-slate-700/50 px-4 py-3">
            <img
              src="/cosellus-logo-white.svg"
              alt="Cosellus"
              className="h-12 w-auto"
            />
            <button
              onClick={() => setMobileOpen(false)}
              className="p-1.5 rounded-md hover:bg-slate-700 focus-visible:outline-2 focus-visible:outline-blue-400"
              aria-label="Close navigation menu"
            >
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>

          {renderNavContent(false)}
          {renderAccountActions(false)}
        </aside>
      </>
    );
  }

  // ── Desktop / Tablet sidebar ────────────────────────────────────────────────

  // Tablet: collapsed by default, expand on hover
  const effectiveCollapsed = mode === 'tablet' ? isCollapsed : isCollapsed;

  return (
    <aside
      ref={sidebarRef}
      data-testid="global-sidebar"
      className={cn(
        /* min-h-0: flex child may shrink so nav scrolls; footer stays visible (layout parent uses overflow:hidden) */
        'flex h-full min-h-0 min-w-0 flex-shrink-0 flex-col self-stretch',
        'border-r border-slate-800',
        'transition-[width] duration-200 ease-in-out',
        effectiveCollapsed ? 'w-[56px]' : 'w-[240px]'
      )}
      style={{ background: 'linear-gradient(180deg, #0f2318 0%, #143024 50%, #1a3a30 100%)', color: '#d1e7dd' }}
      role="complementary"
      aria-label="Global navigation sidebar"
      // Tablet hover: temporarily expand
      onMouseEnter={mode === 'tablet' && isCollapsed ? () => setIsCollapsed(false) : undefined}
      onMouseLeave={mode === 'tablet' ? () => {
        // Restore to localStorage-persisted state
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored === 'true') setIsCollapsed(true);
        } catch { /* noop */ }
      } : undefined}
    >
      {/* Brand header with collapse toggle */}
      <div className={cn(
        'flex shrink-0 items-center border-b border-slate-700/50 px-3 py-3',
        effectiveCollapsed ? 'justify-center' : 'justify-between'
      )}>
        {!effectiveCollapsed ? (
          <>
            <img
              src="/cosellus-logo-white.svg"
              alt="Cosellus"
              className="h-12 w-auto"
            />
            <button
              onClick={toggle}
              className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
              title="Collapse sidebar (Ctrl+B)"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </>
        ) : (
          <button
            onClick={toggle}
            className="p-1 rounded hover:bg-slate-700 text-blue-400 transition-colors"
            title="Expand sidebar (Ctrl+B)"
            aria-label="Expand sidebar"
          >
            <Menu className="h-4 w-4" />
          </button>
        )}
      </div>

      {renderNavContent(effectiveCollapsed)}
      {renderAccountActions(effectiveCollapsed)}
    </aside>
  );
};

export default GlobalSidebar;
